import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import Badge, { BadgeTone } from '../components/ui/Badge';
// import Button from '../components/ui/Button'; // only used by the commented-out gated-approval UI below
import EmptyState from '../components/ui/EmptyState';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';

// Completion no longer waits on a supervisor — the technician's submission
// takes effect immediately (see POST /reports/:id/completion) so the
// citizen isn't stuck waiting on a review queue. 'pending_supervisor' can
// still show up here from before that change (or any not-yet-updated
// client) and keeps its approve/return actions; everything else is a
// look-back log, evidence and summary only.
const STATUS_TONE: Record<string, BadgeTone> = {
  pending_supervisor: 'warning',
  auto_approved: 'success',
  returned: 'danger',
};
const STATUS_LABEL: Record<string, string> = {
  pending_supervisor: 'Pending supervisor',
  auto_approved: 'Completed',
  returned: 'Returned for correction',
};

const ResolutionReviews: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  // 2026-08-13: confirmed with the client that Completion Reviews is
  // intentionally a pure after-the-fact log for supervisors/managers to
  // spot-check evidence — not meant to gate completion. 'busy'/'notes' and
  // decide() below only existed to support the pending_supervisor
  // approve/return UI, which is commented out for the same reason (see
  // below). Left in place, not deleted, in case that gated-approval
  // feature is wanted later — pending_supervisor rows are unreachable
  // today (POST /reports/:id/completion always writes 'auto_approved'),
  // so none of this currently runs regardless.
  // const [busy, setBusy] = useState('');
  // const [notes, setNotes] = useState<Record<string, string>>({});

  const load = () => apiService.get('/reports/resolution-reviews/recent').then((r: any) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  // const decide = async (r: any, decision: 'approve' | 'return') => {
  //   setBusy(r.id);
  //   try {
  //     await apiService.post(`/reports/${r.reportId}/supervisor-decision`, { decision, notes: notes[r.id] });
  //     await load();
  //   } finally {
  //     setBusy('');
  //   }
  // };

  return (
    <div>
      <PageHeader
        title="Completion Reviews"
        subtitle="Technician-submitted work completes immediately — this is a look-back log to spot-check evidence, not an action queue."
      />

      {rows.length === 0 ? (
        <EmptyState icon={ClipboardDocumentCheckIcon} title="No recent completions" description="No work has been completed in the last 30 days." />
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.id} className="rounded-sm border border-gray-300 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex justify-between">
                <div>
                  <b>{r.reportNumber} · {r.reportTitle}</b>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{r.technicianName} · {r.address}</p>
                </div>
                <Badge tone={STATUS_TONE[r.status] ?? 'neutral'}>{STATUS_LABEL[r.status] ?? r.status}</Badge>
              </div>
              <p className="my-3 whitespace-pre-wrap text-sm">{r.completionSummary}</p>
              <div className="flex flex-wrap gap-2">
                {(r.evidence || []).map((e: any, i: number) => (
                  <a key={i} href={typeof e === 'string' ? e : e.url} target="_blank" rel="noreferrer" className="text-sm text-primary-700 underline">
                    Evidence {i + 1}
                  </a>
                ))}
              </div>
              {/* Gated-approval UI, commented out (not deleted) — see the
                  2026-08-13 note above decide(). pending_supervisor is
                  unreachable today, so r.status === 'pending_supervisor'
                  never matches in current use; this whole block is dead
                  weight until/unless that feature is deliberately brought
                  back.
              {r.status === 'pending_supervisor' && (
                <>
                  <textarea
                    value={notes[r.id] || ''}
                    onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                    placeholder="Supervisor notes"
                    className="mt-3 w-full rounded-sm border border-gray-300 p-2"
                  />
                  <div className="mt-3 flex gap-2">
                    <Button
                      disabled={busy === r.id}
                      className="border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800"
                      onClick={() => decide(r, 'approve')}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="danger"
                      disabled={busy === r.id || !notes[r.id]?.trim()}
                      onClick={() => decide(r, 'return')}
                    >
                      Return for correction
                    </Button>
                  </div>
                </>
              )}
              */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResolutionReviews;
